import { createCheckoutSession } from "@/features/billing/api/billing";
import { useFeatureFlags } from "@/features/config/useFeatureFlags";
import { type ReactNode, useState } from "react";
import { toast } from "sonner";
import type { FastQuota, FastTier } from "../types";

export const ENTERPRISE_MAILTO = "mailto:learn@gxl.ai?subject=Paperclip%20Enterprise";
/** What Pro gets, as the backend's plans give it. */
export const PRO_ROWS = 200;
export const PRO_DATASETS = 20;
/** Stands for the plan's quota message in a page's error, which the page draws with its links. */
export const QUOTA_ERROR = "fast-dataset-quota";

const LINK = "font-medium text-zinc-900 underline decoration-zinc-300 underline-offset-2 hover:decoration-zinc-700";

const UpgradeLink = ({ children }: { children: ReactNode }) => {
  const [busy, setBusy] = useState(false);
  return (
    <button
      type="button"
      disabled={busy}
      className={`${LINK} disabled:opacity-60`}
      onClick={() => {
        setBusy(true);
        createCheckoutSession()
          .then(({ checkout_url }) => {
            window.location.href = checkout_url;
          })
          .catch((e: unknown) => {
            toast.error(e instanceof Error ? e.message : "Failed to start checkout.");
            setBusy(false);
          });
      }}
    >
      {children}
    </button>
  );
};

const EnterpriseLink = ({ children }: { children: ReactNode }) => (
  <a href={ENTERPRISE_MAILTO} className={LINK}>
    {children}
  </a>
);

const day = (iso: string) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });

/** Under the table when the owner's plan shows only some of the rows. */
export const RowLimitNote = ({ shown, total, tier, shared }: { shown: number; total: number; tier?: FastTier; shared: boolean }) => {
  const { flags } = useFeatureFlags();
  const counts = `Showing ${shown} of ${total} rows`;
  return (
    <p className="font-ui w-full border-b border-zinc-200 py-3 text-center text-[13px] text-zinc-600" role="status">
      {shared ? (
        `${counts} on the owner's plan.`
      ) : tier === "free" && flags.billing ? (
        <>
          {counts}. <UpgradeLink>Upgrade to Pro</UpgradeLink> to see up to {PRO_ROWS}, or{" "}
          <EnterpriseLink>contact us for Enterprise</EnterpriseLink>.
        </>
      ) : (
        <>
          {counts}. <EnterpriseLink>Contact us for Enterprise</EnterpriseLink> to see every row.
        </>
      )}
    </p>
  );
};

/** The plan's datasets are used up. */
export const QuotaReached = ({ quota }: { quota: FastQuota }) => {
  const { flags } = useFeatureFlags();
  if (quota.tier === "pro") {
    return (
      <span>
        You've used all {quota.limit} Pro datasets this month.{" "}
        {quota.resets_at ? <>More on {day(quota.resets_at)}, or </> : null}
        <EnterpriseLink>{quota.resets_at ? "contact us for Enterprise" : "Contact us for Enterprise"}</EnterpriseLink>.
      </span>
    );
  }
  return flags.billing ? (
    <span>
      You've used all {quota.limit} free datasets. <UpgradeLink>Upgrade to Pro</UpgradeLink> for {PRO_DATASETS} a month,
      or <EnterpriseLink>contact us for Enterprise</EnterpriseLink>.
    </span>
  ) : (
    <span>
      You've used all {quota.limit} free datasets. <EnterpriseLink>Contact us for Enterprise</EnterpriseLink>.
    </span>
  );
};

/** Beside the Build button: how many datasets the plan has left, or that it has none. */
export const QuotaLine = ({ quota }: { quota: FastQuota | undefined }) => {
  if (!quota || quota.remaining === null || quota.limit === null) return null;
  if (quota.remaining === 0) {
    return (
      <span className="mr-auto text-zinc-600" role="status">
        <QuotaReached quota={quota} />
      </span>
    );
  }
  const plan = quota.tier === "pro" ? "Pro" : "free";
  return (
    <span className="mr-auto text-zinc-400">
      {quota.remaining} of {quota.limit} {plan} {quota.limit === 1 ? "dataset" : "datasets"} left
      {quota.period === "month" ? " this month" : ""}
    </span>
  );
};
